import {
  Button,
  Divider,
  DropdownButton,
  DropdownButtonContext,
  EmptyState,
  FlexBox,
  Icon,
  InputText,
  SimplePagination,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useContext, useMemo, useState } from 'react';

import { useMimSidekickChat } from '../../mimSidekickChat.context';

import styles from './mimSidekickHistory.module.scss';
import {
  MOCK_CONVERSATIONS,
  PAGE_SIZE,
  SidekickConversation,
} from './mockConversations';

type MimSidekickHistoryPanelProps = {
  conversations: SidekickConversation[];
};

export const MimSidekickHistoryPanel = ({
  conversations,
}: MimSidekickHistoryPanelProps): JSX.Element => {
  const { startNewConversation, selectConversation } = useMimSidekickChat();
  const { closeDropdown } = useContext(DropdownButtonContext);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return conversations;
    return conversations.filter((c) => c.title.toLowerCase().includes(term));
  }, [conversations, query]);

  const start = page * PAGE_SIZE;
  const visibleItems = matches.slice(start, start + PAGE_SIZE);
  const hasNext = start + PAGE_SIZE < matches.length;
  const isSearching = query.trim().length > 0;

  return (
    <FlexBox flexDirection="column" gap="200" className={styles.panel}>
      <Button
        variant="secondary"
        size="large"
        onClick={() => {
          startNewConversation();
          closeDropdown();
        }}
      >
        New conversation
      </Button>

      <InputText
        icon="search"
        type="search"
        placeholder="Search conversations..."
        value={query}
        aria-label="Search conversations"
        onChange={(e) => {
          setQuery(e.target.value);
          setPage(0);
        }}
      />

      <FlexBox
        justifyContent="space-between"
        alignItems="center"
        padding="0 200"
      >
        <Text.Paragraph as="span" variant="3">
          {isSearching ? 'Search results' : 'Recent conversations'}
        </Text.Paragraph>
        <Text.Paragraph as="span" variant="4" subdued>
          {isSearching ? `${visibleItems.length} matches` : 'Latest first'}
        </Text.Paragraph>
      </FlexBox>

      {/* Remove with the mock feed. */}
      <div className={styles.listArea}>
        <FlexBox flexDirection="column" gap="0" className={styles.list}>
          {visibleItems.length === 0 && (
            <FlexBox justifyContent="center" alignItems="center" flex="1">
              <EmptyState
                icon={null}
                title="No conversations found"
                description="Try a different search."
              />
            </FlexBox>
          )}
          {visibleItems.map((conversation) => (
            <FlexBox
              key={conversation.id}
              as="button"
              gap="200"
              alignItems="center"
              className={classnames(styles.row, {
                [styles.rowCurrent]: conversation.isCurrent,
              })}
              onClick={() => {
                selectConversation(conversation.id);
                closeDropdown();
              }}
            >
              <Icon name="chat" size={16} className={styles.rowIcon} />
              <FlexBox flexDirection="column" className={styles.rowText}>
                <Text.Paragraph
                  as="span"
                  variant="3"
                  className={styles.rowTitle}
                >
                  {conversation.title}
                </Text.Paragraph>
                <Text.Paragraph as="span" variant="4" subdued>
                  {conversation.when}
                  {conversation.isCurrent ? ' · Current' : ''}
                </Text.Paragraph>
              </FlexBox>
            </FlexBox>
          ))}
        </FlexBox>
        <div className={styles.constructionBand}>
          Under construction · mock data
        </div>
      </div>

      <Divider />

      <FlexBox
        justifyContent="space-between"
        alignItems="center"
        padding="0 200"
      >
        <Text.Paragraph as="span" variant="4" subdued>
          {matches.length === 0
            ? '0 of 0'
            : `${start + 1}–${start + visibleItems.length} of ${matches.length}`}
        </Text.Paragraph>
        <SimplePagination
          previousButtonText="Previous"
          nextButtonText="Next"
          previousButtonDisabled={page === 0}
          nextButtonDisabled={!hasNext}
          previousButtonClick={() => setPage((p) => Math.max(0, p - 1))}
          nextButtonClick={() => setPage((p) => p + 1)}
        />
      </FlexBox>
    </FlexBox>
  );
};

export const MimSidekickHistory = (): JSX.Element => (
  <Tooltip content="Conversation history">
    <DropdownButton
      icon="history"
      noArrow
      fixed
      buttonProps={{ variant: 'secondary' }}
      // The floating panel drags by its header, which this dropdown sits inside.
      onPointerDown={(event) => event.stopPropagation()}
      dropdown={<MimSidekickHistoryPanel conversations={MOCK_CONVERSATIONS} />}
    />
  </Tooltip>
);
